import React, { useState, useEffect } from 'react';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PaletteIcon from '@mui/icons-material/Palette';
import GroupsIcon from '@mui/icons-material/Groups';
import { authService } from '../services/api';

const UserProfileModal = ({ isOpen, onClose }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState({});
  const [saving, setSaving] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchProfile();
    }
  }, [isOpen]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const profileData = await authService.getProfile();
      setUser(profileData);
      setEditedUser(profileData);
    } catch (error) {
      console.error('Error fetching profile:', error);
      // Fallback to cached user data
      const currentUser = authService.getCurrentUser();
      if (currentUser) {
        setUser(currentUser);
        setEditedUser(currentUser);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditedUser({ ...user });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedUser(user);
    setShowColorPicker(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setUploadError(null);
      const updatedUser = await authService.updateProfile(editedUser);
      setUser(updatedUser);
      setIsEditing(false);
      setShowColorPicker(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      setUploadError('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image must be less than 5MB');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Only JPEG, PNG and WebP images are allowed');
      return;
    }

    try {
      setUploadingPicture(true);
      setUploadError(null);

      const formData = new FormData();
      formData.append('profilePicture', file);

      const response = await fetch('/api/auth/profile/upload-picture', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Upload failed');
      }

      // Update local state with new picture URL
      setEditedUser({ ...editedUser, profilePicture: result.url });
      setUser({ ...user, profilePicture: result.url });

      // Update localStorage
      const currentUser = authService.getCurrentUser();
      if (currentUser) {
        localStorage.setItem('user', JSON.stringify({
          ...currentUser,
          profilePicture: result.url
        }));
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.message || 'Failed to upload image');
    } finally {
      setUploadingPicture(false);
    }
  };

  const predefinedColors = [
    '#1e514b', '#2196F3', '#4CAF50', '#FF9800', '#9C27B0',
    '#F44336', '#795548', '#607D8B', '#E91E63', '#009688'
  ];

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content profile-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>User Profile</h2>
          <div className="modal-header-actions">
            {!isEditing && (
              <button className="edit-profile-btn" onClick={handleEdit}>
                <EditIcon /> Edit
              </button>
            )}
            <button className="modal-close-btn" onClick={onClose}>
              <CloseIcon />
            </button>
          </div>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
            </div>
          ) : (
            <div className="profile-content">
              <div className="profile-avatar-section">
                <div className="profile-avatar" style={{
                  backgroundColor: isEditing ? editedUser.teamColor || '#1e514b' : user?.teamColor || '#1e514b',
                  position: 'relative'
                }}>
                  {(isEditing ? editedUser.profilePicture : user?.profilePicture) ? (
                    <img
                      src={isEditing ? editedUser.profilePicture : user?.profilePicture}
                      alt="Profile"
                      style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover' }}
                    />
                  ) : (
                    <PersonIcon style={{ fontSize: '60px', color: 'white' }} />
                  )}
                  {isEditing && (
                    <label className="upload-photo-btn" title={uploadingPicture ? 'Uploading...' : 'Upload photo'}>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleImageUpload}
                        disabled={uploadingPicture}
                        style={{ display: 'none' }}
                      />
                      {uploadingPicture ? (
                        <div className="upload-spinner" />
                      ) : (
                        <PhotoCameraIcon style={{ fontSize: '20px' }} />
                      )}
                    </label>
                  )}
                </div>
                {isEditing && (
                  <div className="color-picker-section">
                    <button
                      className="color-picker-toggle"
                      onClick={() => setShowColorPicker(!showColorPicker)}
                      style={{ backgroundColor: editedUser.teamColor || '#1e514b' }}
                    >
                      <PaletteIcon style={{ fontSize: '16px', color: 'white' }} />
                    </button>
                    {showColorPicker && (
                      <div className="color-picker-palette">
                        {predefinedColors.map(color => (
                          <div
                            key={color}
                            className="color-option"
                            style={{ backgroundColor: color }}
                            onClick={() => {
                              setEditedUser({ ...editedUser, teamColor: color });
                              setShowColorPicker(false);
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="profile-info">
                <div className="profile-field">
                  <label className="profile-label">
                    <PersonIcon className="profile-icon" />
                    <span>Name</span>
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      className="profile-input"
                      value={editedUser.name || ''}
                      onChange={(e) => setEditedUser({ ...editedUser, name: e.target.value })}
                      placeholder="Enter your name"
                    />
                  ) : (
                    <div className="profile-value">
                      {user?.name || 'No name provided'}
                    </div>
                  )}
                </div>

                <div className="profile-field">
                  <label className="profile-label">
                    <GroupsIcon className="profile-icon" />
                    <span>Team Name</span>
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      className="profile-input"
                      value={editedUser.teamName || ''}
                      onChange={(e) => setEditedUser({ ...editedUser, teamName: e.target.value })}
                      placeholder="Enter your team name"
                    />
                  ) : (
                    <div className="profile-value">
                      {user?.teamName || 'My Team'}
                    </div>
                  )}
                </div>

                <div className="profile-field">
                  <label className="profile-label">
                    <EmailIcon className="profile-icon" />
                    <span>Email</span>
                  </label>
                  <div className="profile-value">
                    {user?.email || 'No email provided'}
                  </div>
                </div>

                <div className="profile-field">
                  <label className="profile-label">
                    <CalendarTodayIcon className="profile-icon" />
                    <span>Member Since</span>
                  </label>
                  <div className="profile-value">
                    {user?.createdAt 
                      ? new Date(user.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long', 
                          day: 'numeric'
                        })
                      : 'Date not available'
                    }
                  </div>
                </div>
              </div>
              {uploadError && (
                <div className="error-message">
                  {uploadError}
                </div>
              )}
            </div>
          )}
        </div>
        {isEditing && (
          <div className="modal-footer">
            <button className="cancel-btn" onClick={handleCancel} disabled={saving}>
              <CancelIcon /> Cancel
            </button>
            <button className="save-btn" onClick={handleSave} disabled={saving}>
              <SaveIcon /> {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfileModal;